import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Bell,
  Archive,
  ChevronDown,
  ChevronRight,
  FileTerminal,
  Layers,
  ListChecks,
  Menu,
  Monitor,
  PlayCircle,
  LogOut,
  Shield,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { AuthService, type UserProfile } from "@/lib/services/auth";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";


const navigation = [
  { name: "Dashboard", href: "/", icon: Shield },
  { name: "Players", href: "/players", icon: Users },
  { name: "Teams", href: "/teams", icon: Layers },
  { name: "Categories", href: "/categories", icon: Layers },
  { name: "Notifications", href: "/notifications", icon: Bell },
  { name: "Phases", href: "/phases", icon: Layers },
  { name: "Injects", href: "/injects", icon: ListChecks },
  {
    name: "Scenarios",
    href: "/scenarios",
    icon: PlayCircle,
    children: [
      { name: "Scenarios", href: "/scenarios" },
      { name: "Active Scenarios", href: "/scenarios/actives" },
      { name: "Report", href: "/scenarios/reports" },
    ],
  },
  { name: "MITRE ATT&CK Automation", href: "/mitre-attack-automation", icon: FileTerminal },
  { name: "PXP (RLMS)", href: "/pxp-rlms", icon: Monitor },
];

/** Sidebar child "Scenarios" (/scenarios) must not light up for sibling routes (reports, actives, run session, …). */
function isNavChildActive(pathname: string, childHref: string): boolean {
  if (pathname === childHref) return true;

  if (childHref === "/scenarios") {
    const reserved =
      pathname.startsWith("/scenarios/actives") ||
      pathname.startsWith("/scenarios/active/") ||
      pathname.startsWith("/scenarios/reports") ||
      pathname.startsWith("/scenarios/archives") ||
      pathname.startsWith("/scenarios/exercise-archive") ||
      pathname.startsWith("/scenarios/exercise-run") ||
      pathname.startsWith("/scenarios/corporate/actives") ||
      pathname.startsWith("/active-scenarios/corporate/");
    if (reserved) return false;
    return pathname.startsWith("/scenarios/");
  }

  if (childHref === "/scenarios/actives") {
    return (
      pathname === "/scenarios/actives" ||
      pathname.startsWith("/scenarios/active/") ||
      pathname.startsWith("/active-scenarios/corporate/")
    );
  }

  return pathname.startsWith(`${childHref}/`);
}

type AppShellProps = {
  children: React.ReactNode;
};

function userDisplayName(u: UserProfile): string {
  const full = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return full || u.username;
}

function userInitials(u: UserProfile): string {
  const fn = u.first_name?.trim();
  const ln = u.last_name?.trim();
  if (fn && ln) return `${fn[0]}${ln[0]}`.toUpperCase();
  if (fn) return fn.slice(0, 2).toUpperCase();
  if (u.username) return u.username.slice(0, 2).toUpperCase();
  if (u.email) return u.email.slice(0, 2).toUpperCase();
  return "??";
}

export function AppShell({ children }: AppShellProps) {
  const { resolvedTheme } = useTheme();
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { user, logout } = useAuth();

  const onMobileClose = () => setIsSidebarOpen(false);
  const toggleMobileMenu = () => setIsSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    logout();
    AuthService.removeToken();
    window.location.href = "/auth";
  };

  const toggleExpanded = (url: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  useEffect(() => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      let changed = false;
      navigation.forEach((item) => {
        if (item.children?.some((c) => pathname.startsWith(c.href)) && !next.has(item.href)) {
          next.add(item.href);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [pathname]);

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={toggleMobileMenu}
      >
        <Menu className="h-5 w-5" />
      </Button>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
          aria-hidden
        />
      )}

      <div className="relative flex min-h-screen mx-auto max-w-[1920px]">
        <aside
          className={cn(
            "fixed left-0 top-0 z-50 flex h-[100dvh] max-h-[100dvh] w-72 flex-col overflow-hidden border-r border-sidebar-border bg-sidebar transition-transform duration-300",
            "lg:translate-x-0",
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
          aria-label="Primary navigation"
        >
          <div className="flex h-20 shrink-0 items-center justify-between px-6 py-4">
            <Link to="/" className="flex items-center" onClick={onMobileClose}>
              <img
                src="/images/rangestorm-clickable.svg"
                alt="Cyber Range"
                className="h-12 w-auto object-contain hover:scale-105 transition-transform duration-200"
              />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={onMobileClose}
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <nav
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-6"
            aria-label="Main menu"
          >
            <div className="space-y-2.5 pb-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                const hasChildren = !!item.children?.length;
                const isExpanded = expandedItems.has(item.href);
                const isActive =
                  pathname === item.href ||
                  (hasChildren && item.children?.some((c) => pathname.startsWith(c.href)));

                const linkContent = (
                  <>
                    <Icon className="mr-3 h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left">{item.name}</span>
                    {hasChildren &&
                      (isExpanded ? (
                        <ChevronDown className="w-4 h-4 ml-auto" />
                      ) : (
                        <ChevronRight className="w-4 h-4 ml-auto" />
                      ))}
                  </>
                );

                return (
                  <div key={item.name} className="space-y-1">
                    {!hasChildren ? (
                      <Link
                        to={item.href}
                        onClick={onMobileClose}
                        className={cn(
                          "flex items-center w-full justify-start px-3 py-2 rounded-md",
                          isActive
                            ? "bg-gradient-primary text-primary-foreground"
                            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        )}
                      >
                        {linkContent}
                      </Link>
                    ) : (
                      <Button
                        type="button"
                        onClick={() => {
                          if (hasChildren) toggleExpanded(item.href);
                          else navigate(item.href);
                        }}
                        variant={isActive ? "secondary" : "ghost"}
                        className={cn(
                          "w-full justify-start h-11 px-2",
                          isActive
                            ? "bg-secondary text-secondary-foreground"
                            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        )}
                      >
                        {linkContent}
                      </Button>
                    )}

                    {hasChildren && isExpanded && item.children && (
                      <div className="ml-6 space-y-1">
                        {item.children.map((child) => {
                          const isChildActive = isNavChildActive(pathname, child.href);
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              onClick={onMobileClose}
                              className={cn(
                                "flex items-center w-full justify-start text-sm px-3 py-2 rounded-md",
                                isChildActive
                                  ? "bg-gradient-primary text-primary-foreground"
                                  : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                              )}
                            >
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          <div className="flex justify-center items-center">
            <img src={resolvedTheme === "light" ? "/images/bhumiitech_light.svg" : "/images/bhumiitech_dark.svg"} alt="Cyber Range" className="w-[100px]  m-5" />
            <p className="text-xs text-muted-foreground">Copyright © 2026 Bhumiitech</p>
          </div>
          {user && (
            <div className="shrink-0 border-t border-sidebar-border bg-sidebar p-4">
              <button
                type="button"
                className={cn(
                  "flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors",
                  "hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
                  isLoggingOut && "pointer-events-none opacity-50"
                )}
                onClick={handleLogout}
                aria-label={`Sign out ${userDisplayName(user)}`}
                title="Click to sign out"
              >
                <div className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/20">
                  <span className="text-xs font-medium text-primary">
                    {userInitials(user)}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-sidebar-foreground">
                    {userDisplayName(user)}
                  </p>
                  {isLoggingOut ? (
                    <p className="truncate text-xs text-muted-foreground">Signing out…</p>
                  ) : (
                    <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                  )}
                </div>
                {!isLoggingOut && (
                  <LogOut
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                )}
              </button>
            </div>
          )}
        </aside>

        <main className="flex-1 transition-all duration-300 p-6 lg:ml-72">
          {children}
        </main>
      </div>
    </>
  );
}
